import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import {
  ArrowLeft,
  Check,
  Home,
  Mail,
  Phone,
  User,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { DocumentsCard } from "@/components/application/DocumentsCard";
import { NextStepCard } from "@/components/application/NextStepCard";
import { ScoreMeter } from "@/components/application/ScoreMeter";
import { StatusBadge } from "@/components/StatusBadge";
import { useMe } from "@/hooks/api/useAuth";
import { isLandlord } from "@/lib/roles";
import {
  useApplication,
  useCloseApplication,
} from "@/hooks/api/useApplications";

const ACTOR_LABEL: Record<string, string> = {
  candidate: "Candidate",
  agent: "Agency",
  landlord: "Landlord",
  system: "System",
};

const CRITERION_LABEL: Record<string, string> = {
  income: "Income vs rent",
  start_date: "Start date",
  household: "Household size",
  conditions: "House rules",
  completeness: "Completeness",
};

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{value || "—"}</dd>
    </div>
  );
}

type Criterion = { points: number; max: number; note: string; knockouts?: string[] };

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: app, isLoading } = useApplication(id);
  const close = useCloseApplication();
  const { data: me } = useMe();
  const landlordView = isLandlord(me);

  if (isLoading || !app) {
    return <p className="text-sm text-muted-foreground">Loading dossier…</p>;
  }

  const breakdown = (app.score_breakdown ?? {}) as Record<string, unknown>;
  const criteria = Object.entries(CRITERION_LABEL)
    .map(([key, label]) => ({ key, label, c: breakdown[key] as Criterion | undefined }))
    .filter((row) => row.c);
  const fullName = `${app.first_name} ${app.last_name}`.trim();
  const isClosed = Boolean(app.closed_at);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" /> Back
          </button>
          <h1 className="font-display text-3xl font-medium tracking-tight">
            {fullName}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusBadge status={app.status} />
            {app.kind === "question" && <Badge tone="neutral">Question</Badge>}
            {app.migrated_from_enquiry && <Badge tone="neutral">Imported</Badge>}
            <ScoreMeter score={app.score} />
          </div>
        </div>
        <div className="flex gap-2">
        {!landlordView && !isClosed && (
          <Button
            variant="outline"
            disabled={close.isPending}
            onClick={() =>
              close.mutate(
                { id: app.id, reason: "Closed from the dossier" },
                {
                  onSuccess: () => toast.success("Dossier closed"),
                  onError: (e) =>
                    toast.error(e.message || "Could not close this dossier"),
                },
              )
            }
          >
            <Check className="size-4" /> Close dossier
          </Button>
        )}
        </div>
      </header>

      <NextStepCard app={app} landlordView={landlordView} />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="size-4" /> Candidate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field
                  label="Email"
                  value={app.email}
                />
                <Field label="Phone" value={app.phone} />
                <Field label="Language" value={app.language === "nl" ? "Dutch" : "English"} />
                <Field label="Date of birth" value={app.date_of_birth} />
                <Field
                  label="Current address"
                  value={
                    [app.current_street, app.current_house_number]
                      .filter(Boolean)
                      .join(" ") || null
                  }
                />
                <Field label="City" value={app.current_city} />
                <Field label="Living situation" value={app.living_situation} />
                <Field label="Desired move-in" value={app.desired_start_date} />
                <Field
                  label="Household"
                  value={app.household_size != null ? `${app.household_size} person(s)` : null}
                />
                <Field label="Income type" value={app.income_type} />
                <Field
                  label="Gross monthly income"
                  value={
                    app.gross_monthly_income != null
                      ? `€ ${app.gross_monthly_income}`
                      : null
                  }
                />
                <Field
                  label="Rent-to-income"
                  value={
                    app.affordability_pct != null ? `${app.affordability_pct}%` : null
                  }
                />
              </dl>
              <div className="mt-4 flex flex-wrap gap-3 border-t border-border pt-4 text-sm">
                <a
                  href={`mailto:${app.email}`}
                  className="inline-flex items-center gap-1.5 text-primary hover:underline"
                >
                  <Mail className="size-3.5" /> Email candidate
                </a>
                {app.phone && (
                  <a
                    href={`tel:${app.phone}`}
                    className="inline-flex items-center gap-1.5 text-primary hover:underline"
                  >
                    <Phone className="size-3.5" /> Call
                  </a>
                )}
                <Link
                  to={`/properties/${app.property_id}`}
                  className="inline-flex items-center gap-1.5 text-primary hover:underline"
                >
                  <Home className="size-3.5" />
                  {app.property_address ?? "Property"}
                  {app.property_city ? `, ${app.property_city}` : ""}
                </Link>
              </div>
            </CardContent>
          </Card>

          {(app.message || app.motivation) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Message</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {app.message && (
                  <p className="whitespace-pre-line text-sm">{app.message}</p>
                )}
                {app.motivation && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Motivation
                    </p>
                    <p className="mt-1 whitespace-pre-line text-sm">{app.motivation}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {!landlordView && (
            <DocumentsCard
              applicationId={app.id}
              status={app.status}
              incomeType={app.income_type ?? null}
            />
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pre-selection score</CardTitle>
            </CardHeader>
            <CardContent>
              {criteria.length > 0 ? (
                <ul className="space-y-3">
                  {criteria.map(({ key, label, c }) => (
                    <li key={key} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{label}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {c?.note}
                        </p>
                      </div>
                      <span className="shrink-0 text-sm tabular-nums">
                        {c?.points} / {c?.max}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Scored once the candidate submits a full application.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {[...(app.status_events ?? [])].reverse().map((e) => (
                  <li key={e.id} className="flex items-start gap-3">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
                    <div className="min-w-0">
                      <p className="text-sm">
                        {e.from_status ? (
                          <>
                            <StatusBadge status={e.from_status} /> →{" "}
                            <StatusBadge status={e.to_status} />
                          </>
                        ) : (
                          <>
                            Dossier created — <StatusBadge status={e.to_status} />
                          </>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(e.created_at)} ·{" "}
                        {ACTOR_LABEL[e.actor] ?? e.actor}
                        {e.reason ? ` · ${e.reason}` : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
